import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Plus,
  Send,
} from 'lucide-react';
import {
  createAdminNotification,
  fetchAdminNotification,
  fetchAdminNotifications,
  patchAdminNotification,
  type AdminNotificationRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function CreateNotificationModal({
  open,
  onClose,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  categories: Array<{ id: string; label: string }>;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    message: '',
    priority: 'medium',
    categoryId: categories[0]?.id ?? 'platform_announcements',
    examTitle: '',
    organization: '',
    actionRoute: '',
    actionLabel: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      title: '',
      message: '',
      priority: 'medium',
      categoryId: categories[0]?.id ?? 'platform_announcements',
      examTitle: '',
      organization: '',
      actionRoute: '',
      actionLabel: '',
    });
    setError('');
  }, [open, categories]);

  const create = useMutation({
    mutationFn: () =>
      createAdminNotification({
        title: form.title.trim(),
        message: form.message.trim(),
        priority: form.priority,
        categoryId: form.categoryId,
        examTitle: form.examTitle.trim() || undefined,
        organization: form.organization.trim() || undefined,
        actionRoute: form.actionRoute.trim() || undefined,
        actionLabel: form.actionLabel.trim() || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-notifications'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create notification');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="wfx-admin-modal glass-surface" role="dialog" aria-labelledby="create-notification-title" onClick={(e) => e.stopPropagation()}>
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">Platform alerts</p>
            <h2 id="create-notification-title"><Send size={20} /> New notification</h2>
          </div>
        </header>
        <form className="wfx-admin-form" onSubmit={(e) => { e.preventDefault(); setError(''); create.mutate(); }}>
          <label className="wfx-admin-field"><span>Title</span><input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></label>
          <label className="wfx-admin-field"><span>Message</span><textarea required rows={4} className="wfx-admin-textarea" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} /></label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Category</span>
              <select value={form.categoryId} onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}>
                {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Priority</span>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </label>
          </div>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field"><span>Exam title</span><input value={form.examTitle} onChange={(e) => setForm((f) => ({ ...f, examTitle: e.target.value }))} /></label>
            <label className="wfx-admin-field"><span>Organization</span><input value={form.organization} onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))} /></label>
          </div>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field"><span>Action route</span><input value={form.actionRoute} placeholder="/results/..." onChange={(e) => setForm((f) => ({ ...f, actionRoute: e.target.value }))} /></label>
            <label className="wfx-admin-field"><span>Action label</span><input value={form.actionLabel} onChange={(e) => setForm((f) => ({ ...f, actionLabel: e.target.value }))} /></label>
          </div>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>{create.isPending ? 'Sending…' : 'Send notification'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NotificationDetailCard({ notificationId, categories, onClose }: { notificationId: string | null; categories: Array<{ id: string; label: string }>; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-notification', notificationId],
    queryFn: () => fetchAdminNotification(notificationId!),
    enabled: Boolean(notificationId),
  });
  const update = useMutation({
    mutationFn: (body: Partial<AdminNotificationRow>) => patchAdminNotification(notificationId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-notifications'] });
      void qc.invalidateQueries({ queryKey: ['admin-notification', notificationId] });
    },
  });
  const row = data?.notification;

  return (
    <AdminDetailCard open={Boolean(notificationId)} onClose={onClose} ariaLabel="Notification details" breadcrumb="Alert details" title={row?.title ?? 'Loading…'} illustration="admin.module.notifications"
      subtitle={row ? (<><AdminStatusBadge status={row.priority} /><span className="wfx-muted wfx-admin-subline">{row.categoryLabel ?? row.categoryId}</span></>) : undefined}>
      {isLoading && <LoadingState label="Loading notification…" />}
      {isError && <ErrorState message="Could not load notification." onRetry={() => void refetch()} />}
      {row && (
        <>
          <p className="wfx-admin-detail-message">{row.message}</p>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Exam</dt><dd>{row.examTitle ?? '—'}</dd></div>
            <div><dt>Organization</dt><dd>{row.organization ?? '—'}</dd></div>
            <div><dt>Created</dt><dd>{new Date(row.createdAt).toLocaleString()}</dd></div>
            <div><dt>Action</dt><dd>{row.actionRoute ? <Link to={row.actionRoute} className="secondary-action compact">{row.actionLabel ?? 'View'}<ExternalLink size={12} /></Link> : '—'}</dd></div>
          </dl>
          <div className="wfx-admin-form">
            <div className="wfx-admin-form-row">
              <label className="wfx-admin-field"><span>Priority</span><select defaultValue={row.priority} onChange={(e) => update.mutate({ priority: e.target.value })}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></label>
              <label className="wfx-admin-field"><span>Category</span><select defaultValue={row.categoryId ?? ''} onChange={(e) => update.mutate({ categoryId: e.target.value })}>{categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.label}</option>)}</select></label>
            </div>
            <label className="wfx-admin-field"><span>Title</span><input defaultValue={row.title} onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== row.title) update.mutate({ title: v }); }} /></label>
            <label className="wfx-admin-field"><span>Message</span><textarea rows={4} className="wfx-admin-textarea" defaultValue={row.message} onBlur={(e) => { const v = e.target.value.trim(); if (v !== row.message) update.mutate({ message: v }); }} /></label>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function NotificationCard({ row, onSelect }: { row: AdminNotificationRow; onSelect: () => void }) {
  return (
    <button type="button" className="wfx-admin-notification-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head"><AdminStatusBadge status={row.priority} /><span className="wfx-muted wfx-admin-subline">{row.categoryLabel}</span></div>
      <strong>{row.title}</strong>
      <span className="wfx-muted">{row.message.slice(0, 90)}{row.message.length > 90 ? '…' : ''}</span>
      <div className="wfx-admin-page-card-stats"><span>{new Date(row.createdAt).toLocaleDateString()}</span>{row.organization && <span>{row.organization}</span>}</div>
    </button>
  );
}

export function AdminNotificationsScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => { const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 300); return () => window.clearTimeout(t); }, [query]);
  useEffect(() => { setPage(1); }, [debouncedQuery, priorityFilter, categoryFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-notifications', debouncedQuery, priorityFilter, categoryFilter, page],
    queryFn: () => fetchAdminNotifications({ q: debouncedQuery || undefined, priority: priorityFilter, categoryId: categoryFilter, page, limit: 12 }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const categories = data?.categories ?? [];
  const pagination = data?.pagination;
  const kpiItems = useMemo(() => [
    { label: 'Total alerts', value: stats?.total ?? 0 },
    { label: 'Critical', value: stats?.critical ?? 0 },
    { label: 'High priority', value: stats?.high ?? 0 },
    { label: 'Categories', value: stats?.categories ?? 0 },
  ], [stats]);

  if (isLoading && !data) return <LoadingState label="Loading notifications…" />;
  if (isError) return <ErrorState message="Could not load notifications." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout breadcrumb="Admin / Notifications" title="Notifications" description="Manage platform-wide exam alerts, result updates, and student notifications." icon={Bell} illustration="admin.module.notifications"
        actions={<button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}><Plus size={16} /> New notification</button>}>
        <AdminKpiStrip items={kpiItems} />
        <div className="wfx-admin-blog-grid">{rows.map((row) => <NotificationCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />)}</div>
        {rows.length === 0 && <AdminEmptyState message="No notifications match your filters." />}
        <AdminTablePanel title="Notification feed" toolbar={(
          <div className="wfx-admin-toolbar">
            <AdminSearchInput value={query} onChange={setQuery} placeholder="Search title, exam, or organization…" />
            <select className="wfx-admin-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Filter by category">
              <option value="all">All categories</option>{categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
            </select>
            <select className="wfx-admin-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} aria-label="Filter by priority">
              <option value="all">All priorities</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
        )} footer={pagination && pagination.pages > 1 ? (
          <div className="wfx-admin-pagination">
            <button type="button" className="secondary-action compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={16} /> Previous</button>
            <span className="wfx-muted">Page {pagination.page} of {pagination.pages} · {pagination.total} alerts</span>
            <button type="button" className="secondary-action compact" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next <ChevronRight size={16} /></button>
          </div>
        ) : undefined}>
          <table className="wfx-admin-table"><thead><tr><th>Title</th><th>Category</th><th>Priority</th><th>Organization</th><th>Created</th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.id} className="wfx-admin-table-row-clickable" onClick={() => setSelectedId(row.id)}>
                <td><strong>{row.title}</strong></td><td>{row.categoryLabel}</td><td><AdminStatusBadge status={row.priority} /></td><td>{row.organization ?? '—'}</td><td>{new Date(row.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>
      <CreateNotificationModal open={createOpen} onClose={() => setCreateOpen(false)} categories={categories} />
      <NotificationDetailCard notificationId={selectedId} categories={categories} onClose={() => setSelectedId(null)} />
    </>
  );
}
